import Button from '@atoms/Button/Button';
import RichText from '@atoms/RichText/RichText';
import useTranslations from '@hooks/useTranslations';
import clsx from 'clsx';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';

import styles from './CollapsibleText.module.scss';

interface Props {
  id?: string;
  text: string;
  isCollapsible: boolean;
  className?: string;
  store?: boolean;
}

const DEFAULT_HEIGHT = 320;

export default function CollapsibleText({
  id,
  text,
  isCollapsible,
  className,
  store,
  children,
}: PropsWithChildren<Props>) {
  const t = useTranslations();
  const ref = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [height, setHeight] = useState<'auto' | number>('auto');

  const handleClick = () => {
    setIsOpen(state => !state);
  };

  // Toggle height when open/close
  useEffect(() => {
    const textHeight = ref.current?.scrollHeight || 0;

    if (!isOpen) {
      setHeight(textHeight > DEFAULT_HEIGHT ? DEFAULT_HEIGHT : 'auto');
    } else {
      setHeight(textHeight || DEFAULT_HEIGHT);
    }
  }, [isOpen]);

  // Listen for resize
  useEffect(() => {
    const handleResize = () => {
      if (!isOpen) return;

      setHeight(ref.current?.scrollHeight || DEFAULT_HEIGHT);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  if (!isCollapsible || !id) {
    return (
      <>
        <RichText text={text} className={className} store={store} />
        {children}
      </>
    );
  }

  const wrapperId = `collapsible-text-${id}`;

  return (
    <div
      className={clsx(
        styles.container,
        className,
        height !== 'auto' && !isOpen && styles.collapsed,
      )}
    >
      <div className={styles.wrapper} style={{ height }} id={wrapperId}>
        <div ref={ref}>
          <RichText className={styles.text} text={text} store={store} />
          {children}
        </div>
      </div>

      {height !== 'auto' && (
        <Button
          className={styles.button}
          label={isOpen ? t.readLess : t.readMore}
          variant="text"
          onClick={handleClick}
          aria-expanded={isOpen}
          aria-controls={wrapperId}
          noAnimation
          icon={isOpen ? 'NAV_ARROW_UP' : 'NAV_ARROW_DOWN'}
        />
      )}
    </div>
  );
}
